import { useFormikContext } from 'formik'
import { IFormikUserModel } from '@user/components/Settings/Users/UpdateUser/typings'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ISalesTeam, selectAllSalesTeams } from '@user/store/salesTeam'
import { useGetEmployeeListOnMount } from '@user/store/employee'
import { useSelector } from 'react-redux'
import { selectAllRoles } from '@user/store/roles/slice'
import { Role } from '@user/store/roles/typings'
import { adminRoleName } from '@user/components/Settings/Users/UpdateUser/constants'
import { ITenantUser } from '@user/store/users'

export interface IUpdateUserProps {
  reopenDrawerOpen: boolean
  userType: 'mapped' | 'unmapped' | undefined
  setInitialActive?: React.Dispatch<React.SetStateAction<boolean>>
  onClose: () => void
  user: ITenantUser | undefined
}

export const useData = ({
  reopenDrawerOpen,
  userType,
  setInitialActive,
  onClose,
  user,
}: IUpdateUserProps) => {
  // ========== S T A T E ==========
  const [filteredSalesTeams, setFilteredSalesTeams] = useState<ISalesTeam[]>([])
  const [open, setOpen] = useState<boolean>(reopenDrawerOpen)
  const [openReAssignDrawerAfterSave, setOpenReAssignDrawer] =
    useState<boolean>(false)
  const [reassignLeads, setReassignLeads] = useState<boolean>(false)
  const [reassignOpps, setReassignOpps] = useState<boolean>(false)
  const [title, setTitle] = useState<string>('')
  const [variant, setVariant] = useState<'mapped' | 'unmapped' | undefined>()
  const [reAssignDrawerOpen, setReAssignDrawerOpen] = useState(false)

  // ========== S T O R E ==========
  const allRoles = useSelector(selectAllRoles)
  const allSalesTeams = useSelector(selectAllSalesTeams)
  const loadingEmployeeList = useGetEmployeeListOnMount(
    undefined,
    variant === 'mapped',
  )

  const {
    setFieldValue,
    resetForm,
    submitForm,
    validateForm,
    isValid,
    setFormikState,
    setFieldTouched,
    values: { externalUser, role, isActive, salesTeamAssignments },
  } = useFormikContext<IFormikUserModel>()

  // ========== C O N S T S ==========
  const existingRole = useMemo(() => {
    return allRoles.find((r) => r.name === user?.roleName)
  }, [allRoles, user?.roleName])

  const existingRolePermission = useMemo(
    () =>
      ['leads_own', 'opportunities_own'].map(
        (permOp) =>
          existingRole?.name === adminRoleName ||
          existingRole?.permissions?.includes(permOp),
      ),
    [existingRole?.name, existingRole?.permissions],
  )
  const newRolePermission = useMemo(
    () =>
      ['leads_own', 'opportunities_own'].map(
        (permOp) =>
          role?.name === adminRoleName || role?.permissions?.includes(permOp),
      ),
    [role?.name, role?.permissions],
  )

  const disableChangeRole = useMemo(() => {
    return false
  }, [])

  // ========== E V E N T S ==========
  const handleClose = useCallback(() => {
    resetForm()
    onClose()

    if (setInitialActive) setInitialActive(false)
  }, [onClose, resetForm, setInitialActive])

  const handleRoleChange = useCallback(
    (role: Role | null) => {
      setFieldValue('role', role)
      setFieldValue('roleName', role?.name)
    },
    [setFieldValue],
  )

  const handleSubmit = useCallback(() => {
    if (openReAssignDrawerAfterSave) {
      if (isValid) {
        setReAssignDrawerOpen(true)
      } else {
        setFieldTouched('salesTeamAssignments', true, true)
        if (salesTeamAssignments.length > 0) {
          setFormikState((prevState) => ({
            ...prevState,
            submitCount: prevState.submitCount + 1,
          }))
        }
        validateForm()
      }
    } else {
      submitForm()
    }
  }, [
    isValid,
    openReAssignDrawerAfterSave,
    salesTeamAssignments.length,
    setFieldTouched,
    setFormikState,
    submitForm,
    validateForm,
  ])

  const onReAssignSuccess = useCallback(async () => {
    console.log('called onReAssignSuccess')
    await setFieldValue('isActive', false)
  }, [setFieldValue])

  const onGuestUserReAssignSuccess = useCallback(() => {
    submitForm()
  }, [submitForm])

  // ========== H O O K S ==========
  useEffect(() => {
    setVariant(userType)
  }, [userType])

  useEffect(() => {
    setOpen(reopenDrawerOpen)
  }, [reopenDrawerOpen])

  useEffect(() => {
    let t: string
    if (user) {
      t = 'Edit'
    } else {
      t = 'Add'
    }

    if (userType === 'mapped') {
      t += ' Mapped'
    } else {
      t += ' Unmapped'
    }

    t += ' User'

    setTitle(t)
  }, [user, userType])

  useEffect(() => {
    if (user) {
      setOpen(true)
    }
  }, [user])

  const setEditUserInitialValues = useCallback(
    async (user: ITenantUser) => {
      await setFieldValue('id', user.id)
      if (user.roleId) {
        const role: Role | undefined = allRoles.find(
          (x) => x?.id == user?.roleId,
        )
        await setFieldValue('role', role)
      } else {
        await setFieldValue('role', undefined)
      }
      if (userType === 'unmapped') {
        await setFieldValue('originalEmailAddress', user.emailAddress)
      }
    },
    [allRoles, setFieldValue, userType],
  )

  useEffect(() => {
    if (user?.id) {
      setEditUserInitialValues(user)
    } else {
      setFieldValue('id', '')
    }
  }, [
    allRoles,
    user?.emailAddress,
    user?.id,
    user?.roleId,
    userType,
    setFieldValue,
  ])

  useEffect(() => {
    if (externalUser) {
      setFilteredSalesTeams(
        allSalesTeams.filter(
          (s) =>
            externalUser.branchIds.findIndex((b) => b == s.branchId) !== -1,
        ),
      )
    } else {
      setFilteredSalesTeams([])
    }
  }, [allSalesTeams, externalUser])

  useEffect(() => {
    const [origLeads, origOpps] = existingRolePermission
    const [newLeads, newOpps] = newRolePermission

    let showSideSheet = false
    let openLeads = false
    let openOpps = false

    if (origLeads && !newLeads && Number(user?.openLeadCount) > 0) {
      showSideSheet = true
      openLeads = true
    }

    if (origOpps && !newOpps && Number(user?.openOppCount) > 0) {
      showSideSheet = true
      openOpps = true
    }
    setOpenReAssignDrawer(showSideSheet)
    setReassignLeads(openLeads)
    setReassignOpps(openOpps)
  }, [existingRolePermission, newRolePermission])

  return {
    filteredSalesTeams,
    open,
    title,
    variant,
    reAssignDrawerOpen,
    setReAssignDrawerOpen,
    externalUser,
    role,
    isActive,
    loadingEmployeeList,
    allRoles,
    allSalesTeams,
    handleClose,
    onGuestUserReAssignSuccess,
    onReAssignSuccess,
    handleSubmit,
    handleRoleChange,
    disableChangeRole,
    openReAssignDrawerAfterSave,
    reassignLeads,
    reassignOpps,
  }
}
